import { mapStatus } from '@/Pages/InsightsJobConfig/utils/mapStatus';
import { formatDate } from '@/Pages/InsightsJobs/utils';
import { RunStatusIcon } from '@/components/RunStatus';
import { Skeleton } from '@/components/Skeleton';
import { Text } from '@/design-system';
import { RunSchema } from '@/types/schema';

import { InsightsJobStatsSchema } from '../types';

const formatter = new Intl.NumberFormat('en-US', {});

interface StatItemProps<T> {
  label: React.ReactNode;
  value?: T;
  isLoading?: boolean;
  renderItem: (v: T) => React.ReactNode;
}

function StatItem<T>({
  label,
  value,
  isLoading,
  renderItem,
}: StatItemProps<T>) {
  const hasValue =
    value != null && (Array.isArray(value) ? value.length > 0 : true);

  if (!isLoading && !hasValue) return null; // hide completely

  return (
    <div className="flex flex-col gap-1">
      <div className="whitespace-nowrap text-xs font-medium tracking-tight text-quaternary">
        {label}
      </div>
      <div className="font-medium">
        {isLoading ? <Skeleton className="m-0 h-5 w-16" /> : renderItem(value!)}
      </div>
    </div>
  );
}

export function InsightsJobStats({
  stats,
  isLoadingStats,
}: {
  stats?: InsightsJobStatsSchema;
  isLoadingStats: boolean;
}) {
  return (
    <div className={'flex flex-col gap-1 text-sm'}>
      <div className={'flex flex-col gap-4 p-4'}>
        <Text variant="h3">Insight Overview</Text>

        {/* Status */}
        <StatItem
          label="Status"
          value={stats?.status}
          isLoading={isLoadingStats}
          renderItem={(v) => {
            const mappedStatus = mapStatus(v);
            const runLikeObject = {
              status: mappedStatus,
              start_time: stats?.startTime,
              end_time: stats?.endTime,
            } as Pick<RunSchema, 'status' | 'start_time' | 'end_time'>;
            return (
              <div className="flex items-center gap-1">
                <RunStatusIcon run={runLikeObject as RunSchema} />
                <Text className="capitalize" color="secondary">
                  {mappedStatus}
                </Text>
              </div>
            );
          }}
        />

        {/* Start time */}
        <StatItem
          label="Start Time"
          value={stats?.startTime}
          isLoading={isLoadingStats}
          renderItem={(v) => <Text variant="sm">{formatDate(v)}</Text>}
        />

        {/* End time */}
        <StatItem
          label="End Time"
          value={stats?.endTime}
          isLoading={isLoadingStats}
          renderItem={(v) => <Text variant="sm">{formatDate(v)}</Text>}
        />

        {/* Runs sampled */}
        <StatItem
          label="Runs Sampled"
          value={stats?.runsSampled}
          isLoading={isLoadingStats}
          renderItem={(v) => (
            <span data-testid="project-stats-sample-size">
              {formatter.format(v as number)}
            </span>
          )}
        />

        {/* Attributes */}
        <StatItem
          label="Attributes"
          value={stats?.attributes}
          isLoading={isLoadingStats}
          renderItem={(v) => (
            <Text variant="sm">{Object.keys(v).join(', ')}</Text>
          )}
        />

        {/* Summary prompt */}
        <StatItem
          label="Summary Prompt"
          value={stats?.summaryPrompt}
          isLoading={isLoadingStats}
          renderItem={(v) => {
            if (!v) return null;
            return (
              <div className="max-h-[400px] overflow-y-auto whitespace-pre-wrap rounded-md bg-tertiary p-2">
                <Text variant="sm" className="text-secondary">
                  {v}
                </Text>
              </div>
            );
          }}
        />
      </div>
    </div>
  );
}
